import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ATELIER_LAYOUTS, getLayoutLabel, getLayoutDescription
} from './atelierLayouts';
import { getOverlayGeometry } from './atelierLayoutGeometry';
import { FORMAT_DIMENSIONS_MM } from '../../../utils/printFormats';

// Colonne gauche de l'atelier (onglet "Mise en page") — jamais de
// positionnement libre (cahier des charges §4).
//
// REFONTE (phase 3) : la galerie de TOUTES les dispositions est affichee
// d'emblee, en permanence — plus de bouton "Changer de mise en page", plus de
// miniature d'emplacements ici (poser une photo ou un texte se fait sur la page
// elle-meme, voir AtelierPageOverlay). Un clic applique la disposition ; si la
// page avait du contenu, un message flottant propose "Annuler".
//
// Regroupement par NOMBRE DE PHOTOS : 1, 2, 3, 4 et plus, texte, double page.
// Chaque carte est rendue avec les VRAIES photos de l'utilisateur (variees
// d'une carte a l'autre) ; sans photo, des pictogrammes. Une disposition
// impossible est grisee avec une raison courte ("Il faut 3 photos").

// Alignees sur backend/services/composition/coverFormat.js (COVER_FORMATS).

// Pictogramme PAR TYPE D'EMPLACEMENT (photo/texte/titre) — pas une vraie
// photo. REVERTI le 2026-10-11 (retour utilisateur, apres avoir vu la
// version a vraies photos a l'ecran : "les apercus des templates [...]
// doivent montrer des icones de photos la ou il y'a des photos, du texte la
// ou il y'a du texte, des titres la ou il y'a des titres [...] au lieu de
// mettre des vraies photos et de les propager partout" — les memes quelques
// photos de la bibliotheque, repetees sur chacune des ~20 cartes de la
// galerie, faisaient doublon/desordre a l'ecran plutot que d'aider a
// reconnaitre une mise en page). Chaque rectangle garde sa position reelle
// (LAYOUT_GEOMETRY, meme source de verite que l'incrustation sur la page et
// que LayoutFormatMiniature) mais porte desormais un simple pictogramme
// centre au lieu d'un fond photo.
function SlotTypeIcon({ type }) {
  if (type === 'photo') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <path d="m21 16-5-5-8 8" />
      </svg>
    );
  }
  if (type === 'title') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6h12M12 6v12" />
      </svg>
    );
  }
  // 'text'
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M5 7h14M5 12h14M5 17h9" />
    </svg>
  );
}

function LayoutPreviewBox({ slug, slots, spread, aspectRatio, photoUrls }) {
  const geometry = getOverlayGeometry(slug);
  let photoCursor = 0;
  const nextPhoto = () => {
    if (!photoUrls || photoUrls.length === 0) return null;
    const url = photoUrls[photoCursor % photoUrls.length];
    photoCursor += 1;
    return url;
  };

  // Double page : une seule photo etalee sur deux feuillets cote a cote — se
  // lit comme "cette mise en page prend deux pages".
  if (spread) {
    const url = nextPhoto();
    const half = (side) => ({
      className: `atelier-layout-preview-rect is-photo ${url ? 'has-image' : ''}`,
      style: url ? { backgroundImage: `url('${url}')`, backgroundSize: '200% 100%', backgroundPosition: side === 'left' ? 'left center' : 'right center' } : undefined
    });
    return (
      <div className="atelier-layout-preview is-spread" style={{ aspectRatio: aspectRatio * 2 }}>
        <span {...half('left')}>{!url && <SlotTypeIcon type="photo" />}</span>
        <span {...half('right')}>{!url && <SlotTypeIcon type="photo" />}</span>
      </div>
    );
  }

  const fallbackRect = (index) => {
    const h = 100 / slots.length;
    return { top: index * h, left: 0, width: 100, height: h - 2 };
  };

  return (
    <div className="atelier-layout-preview" style={{ aspectRatio }}>
      {slots.map((slotType, index) => {
        const rect = (geometry && geometry[index]) || fallbackRect(index);
        const style = { top: `${rect.top}%`, left: `${rect.left}%`, width: `${rect.width}%`, height: `${rect.height}%` };
        const url = slotType === 'photo' ? nextPhoto() : null;
        return (
          <span
            key={index}
            className={`atelier-layout-preview-rect ${slotType === 'photo' ? 'is-photo' : 'is-text'} ${url ? 'has-image' : ''}`}
            style={url ? { ...style, backgroundImage: `url('${url}')` } : style}
          >
            {!url && <SlotTypeIcon type={slotType} />}
          </span>
        );
      })}
    </div>
  );
}

// Nombre d'emplacements photo d'une mise en page (une double page = 1 photo).
export const photoSlotCount = (layout) => (layout.spread ? 1 : layout.slots.filter((s) => s === 'photo').length);

// Groupe d'une mise en page : double page, texte seul, ou nombre de photos.
export function layoutGroupOf(layout) {
  if (layout.spread) return 'double';
  const photos = photoSlotCount(layout);
  if (photos === 0) return 'text';
  if (photos === 1) return 'one';
  if (photos === 2) return 'two';
  if (photos === 3) return 'three';
  return 'fourPlus';
}

export const LAYOUT_GROUP_ORDER = ['one', 'two', 'three', 'fourPlus', 'text', 'double'];

// Pourquoi une mise en page est-elle impossible ici ? null = possible.
// `photosCount` : photos de la BIBLIOTHEQUE (une photo peut servir plusieurs
// fois) ; `doublePagePossible` : la page a-t-elle une page en face ?
export function incompatibilityOf(layout, { photosCount, doublePagePossible }) {
  if (layout.spread && !doublePagePossible) return { code: 'noDoublePage' };
  const needed = photoSlotCount(layout);
  if (needed > photosCount) return { code: 'needPhotos', count: needed };
  return null;
}

// PHASE 8 (2026-10-16, docs/atelier-mises-en-page.html) — panneau simplifie, DOUZE modeles :
// filtres par nombre de photos, section « Recommandes pour cette page », coche sur le modele actif, vignettes avec les
// photos de la page quand elles existent, et « Texte seul » avec ses prereglages (Libre, Dedicace, Chapitre,
// Remerciements). Plus de « Style des photos », plus de « Remplir tous les cadres » : le cadrage se regle photo par photo.
export const GALLERY_GROUP_ORDER = ['one', 'two', 'three', 'fourPlus', 'text', 'double'];
export const galleryGroupOf = (layout) => layout.galleryGroup || layoutGroupOf(layout);

// Score de recommandation d'un modele pour la page courante : meme nombre de photos (+10), nombre voisin (+3),
// orientation dominante des photos de la page (+4) ; « Texte seul » (-5) et double page (-2) ne sont jamais en tete.
// Aucune recommandation si la page n'a aucune photo.
export function recommendationScore(layout, { photoCount, orientation }) {
  if (!photoCount) return 0;
  const needed = photoSlotCount(layout);
  let score = 0;
  if (needed === photoCount) score += 10;
  else if (Math.abs(needed - photoCount) === 1) score += 3;
  if (Boolean(layout.vertical) === (orientation === 'vertical')) score += 4;
  if (needed === 0) score -= 5;
  if (layout.spread) score -= 2;
  return score;
}

// Orientation dominante des photos de la page (verticale en cas d'egalite : les photos de telephone).
export function dominantOrientation(items) {
  let vertical = 0;
  let horizontal = 0;
  (items || []).forEach((item) => {
    const w = item && item.metadata && item.metadata.width;
    const h = item && item.metadata && item.metadata.height;
    if (!(w > 0 && h > 0)) return;
    if (h >= w) vertical += 1; else horizontal += 1;
  });
  return horizontal > vertical ? 'horizontal' : 'vertical';
}

function LayoutGalleryCard({ layout, selected, onChoose, aspectRatio, photoUrls, reason, recommended, sample, t }) {
  const disabled = Boolean(reason);
  return (
    <button
      type="button"
      className={`atelier-layout-card ${disabled ? 'is-disabled' : ''} ${selected ? 'is-current' : ''}`}
      aria-pressed={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onChoose(layout.slug)}
    >
      {selected && (
        <span className="atelier-layout-card-now" role="img" aria-label={t('layoutPanel.panel.active')}>
          <svg viewBox="0 0 16 16" width="13" height="13"><path d="m3 8.5 3.2 3.2L13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
      )}
      {recommended && <span className="atelier-layout-card-rec">{t('layoutPanel.panel.ideal')}</span>}
      {sample != null ? (
        <div className="atelier-layout-preview" style={{ aspectRatio }}>
          <span className="atelier-layout-sample">{sample}</span>
        </div>
      ) : (
        <LayoutPreviewBox slug={layout.slug} slots={layout.slots} spread={layout.spread} aspectRatio={aspectRatio} photoUrls={photoUrls} />
      )}
      <b className="atelier-layout-card-label">{getLayoutLabel(layout.slug, t)}</b>
      {disabled
        ? <span className="atelier-layout-card-reason">{reason}</span>
        : <span className="atelier-layout-card-caption">{getLayoutDescription(layout.slug, t)}</span>}
    </button>
  );
}

function LayoutGallery({
  layouts, selectedSlug, draftPageKind, onChoose, onChoosePageKind, printFormat, pagePhotos, photosCount, doublePagePossible, t
}) {
  const [filter, setFilter] = React.useState('all');
  const dims = FORMAT_DIMENSIONS_MM[printFormat] || FORMAT_DIMENSIONS_MM.standard;
  const aspectRatio = dims.widthMm / dims.heightMm;
  const photoUrls = (pagePhotos || []).map((photo) => photo.metadata?.thumbnailUrl || photo.url).filter(Boolean);
  const reasonOf = (layout) => {
    const incompatibility = incompatibilityOf(layout, { photosCount, doublePagePossible });
    if (!incompatibility) return null;
    return incompatibility.code === 'noDoublePage'
      ? t('layoutPanel.noDoublePage')
      : t('layoutPanel.needPhotos', { count: incompatibility.count });
  };
  const urlsFor = (layout) => {
    const count = Math.max(1, photoSlotCount(layout));
    return photoUrls.length > 0 ? Array.from({ length: count }, (_, i) => photoUrls[i % photoUrls.length]) : [];
  };
  const preset = draftPageKind || 'libre';
  const isTextOnly = (layout) => layout.slug === 'ONE_TESTIMONY';
  const isSelected = (layout) => (isTextOnly(layout) ? (selectedSlug === layout.slug || Boolean(draftPageKind)) : selectedSlug === layout.slug);

  const orientation = dominantOrientation(pagePhotos);
  const photoCount = (pagePhotos || []).length;
  const recommended = photoCount > 0
    ? layouts
      .filter((layout) => !reasonOf(layout))
      .map((layout) => ({ layout, score: recommendationScore(layout, { photoCount, orientation }) }))
      .filter((entry) => entry.score >= 7)
      .sort((x, y) => y.score - x.score)
      .slice(0, 2)
      .map((entry) => entry.layout)
    : [];

  const renderCard = (layout, isRec) => (
    <React.Fragment key={`${isRec ? 'rec-' : ''}${layout.slug}`}>
      <LayoutGalleryCard
        layout={layout}
        selected={isSelected(layout)}
        onChoose={onChoose}
        aspectRatio={aspectRatio}
        photoUrls={urlsFor(layout)}
        reason={reasonOf(layout)}
        recommended={isRec}
        sample={isTextOnly(layout) && !isRec ? t(`layoutPanel.panel.samples.${preset}`) : null}
        t={t}
      />
      {isTextOnly(layout) && !isRec && isSelected(layout) && (
        <div className="atelier-layout-presets">
          <small>{t('layoutPanel.panel.preset')}</small>
          <div className="atelier-layout-chips" role="group" aria-label={t('layoutPanel.panel.preset')}>
            {['libre', 'dedication', 'chapter-divider-manuel', 'thank-you'].map((key) => (
              <button
                key={key}
                type="button"
                className="atelier-layout-chip"
                aria-pressed={preset === key}
                onClick={() => (key === 'libre' ? onChoose('ONE_TESTIMONY') : onChoosePageKind(key))}
              >
                {t(`layoutPanel.panel.presets.${key}`)}
              </button>
            ))}
          </div>
        </div>
      )}
    </React.Fragment>
  );

  return (
    <div className="atelier-layout-gallery">
      <p className="atelier-layout-sub">{photoCount === 0 ? t('layoutPanel.panel.subtitle_none') : t('layoutPanel.panel.subtitle', { count: photoCount })}</p>
      <div className="atelier-layout-chips" role="group" aria-label={t('layoutPanel.panel.filterLabel')}>
        {['all', ...GALLERY_GROUP_ORDER].map((key) => (
          <button key={key} type="button" className="atelier-layout-chip" aria-pressed={filter === key} onClick={() => setFilter(key)}>
            {t(`layoutPanel.panel.filters.${key}`)}
          </button>
        ))}
      </div>
      {filter === 'all' && recommended.length > 0 && (
        <div className="atelier-layout-group">
          <h2>{t('layoutPanel.panel.recommended')}</h2>
          <div className="atelier-layout-grid">{recommended.map((layout) => renderCard(layout, true))}</div>
        </div>
      )}
      {GALLERY_GROUP_ORDER
        .filter((id) => filter === 'all' || filter === id)
        .map((id) => ({ id, items: layouts.filter((layout) => galleryGroupOf(layout) === id) }))
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <div key={group.id} className="atelier-layout-group">
            <h2>{t(`layoutPanel.groups.${group.id}`)}</h2>
            <div className="atelier-layout-grid">{group.items.map((layout) => renderCard(layout, false))}</div>
          </div>
        ))}
    </div>
  );
}

function AtelierLayoutPanel({
  draftLayoutSlug,
  onChooseLayout,
  printFormat,
  // Slugs REELLEMENT presents dans layout_definitions : proposer une mise en page que la base ne connait pas
  // afficherait un bouton qui echoue a l'enregistrement. Non fourni -> aucun filtre.
  availableSlugs,
  // Une double page exige une page EN FACE (la page 1 est seule a droite, la derniere peut tomber a gauche).
  doublePagePossible = true,
  // Pages "sans emplacement" (phase31) — mutuellement exclusif avec draftLayoutSlug ; ce sont desormais des
  // prereglages du modele « Texte seul ».
  draftPageKind,
  draftPageKindFields,
  onChoosePageKind,
  onPageKindFieldChange,
  photos,
  // Elements photo de LA PAGE courante (vignettes reelles, orientation pour les recommandations).
  pagePhotos = []
}) {
  const { t } = useTranslation('atelier');
  const galleryLayouts = ATELIER_LAYOUTS.filter((layout) => !layout.legacy && (!availableSlugs || availableSlugs.has(layout.slug)));
  const photosCount = (photos || []).length;

  return (
    <div className="atelier-layout-panel">
      <div className="atelier-layout-panel-head">
        <span className="atelier-layout-panel-title">{t('layoutPanel.title')}</span>
      </div>

      <LayoutGallery
        layouts={galleryLayouts}
        selectedSlug={draftLayoutSlug}
        draftPageKind={draftPageKind}
        onChoose={onChooseLayout}
        onChoosePageKind={onChoosePageKind}
        printFormat={printFormat}
        pagePhotos={pagePhotos}
        photosCount={photosCount}
        doublePagePossible={doublePagePossible}
        t={t}
      />
    </div>
  );
}

export default AtelierLayoutPanel;
